import { useEffect } from 'react';
import './PowerGemAnimation.css';

/**
 * PROFESSIONAL POWER GEM ANIMATION
 * Rock-type special move - fires rays of dazzling gemstone light
 * 
 * Key Features:
 * - Rock-type special attack (80 base power, 100% accuracy)
 * - Brilliant gemstone energy beams
 * - Professional gaming quality with crystalline light effects
 */
export default function PowerGemAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Gem Energy Charge (0-500ms)
    playerEl.classList.add('power-gem-user-charge');

    const gemAura = document.createElement('div');
    gemAura.className = 'power-gem-aura';
    gemAura.style.left = `${playerX - 70}px`;
    gemAura.style.top = `${playerY - 70}px`;
    container.appendChild(gemAura);
    elements.push(gemAura);

    // Floating gem crystals
    for (let i = 0; i < 10; i++) {
      const gem = document.createElement('div');
      gem.className = 'power-gem-crystal';
      const angle = (i / 10) * 360;
      const radius = 55;
      gem.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 5}px`;
      gem.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 5}px`;
      gem.style.animationDelay = `${i * 40}ms`;
      container.appendChild(gem);
      elements.push(gem);
    }

    // PHASE 2: Gem Beam Launch (500-1000ms)
    const phase2Timer = setTimeout(() => {
      // Multiple gem beams
      for (let i = 0; i < 5; i++) {
        const beamTimer = setTimeout(() => {
          const beam = document.createElement('div');
          beam.className = 'power-gem-beam';
          const offsetX = (Math.random() - 0.5) * 20;
          const offsetY = (Math.random() - 0.5) * 20;
          beam.style.left = `${playerX + offsetX}px`;
          beam.style.top = `${playerY + offsetY}px`;
          beam.style.setProperty('--dx', `${dx - offsetX}px`);
          beam.style.setProperty('--dy', `${dy - offsetY}px`);
          container.appendChild(beam);
          elements.push(beam);
        }, i * 50);
        elements.push({ timer: beamTimer, type: 'timer' });
      }

      // Sparkle particles along path
      for (let i = 0; i < 16; i++) {
        const sparkleTimer = setTimeout(() => {
          const sparkle = document.createElement('div');
          sparkle.className = 'power-gem-sparkle';
          const progress = i / 16;
          sparkle.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 25 - 4}px`;
          sparkle.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 25 - 4}px`;
          container.appendChild(sparkle);
          elements.push(sparkle);
        }, i * 25);
        elements.push({ timer: sparkleTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Gem Impact (1000-1500ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('power-gem-opponent-hit');

      const explosion = document.createElement('div');
      explosion.className = 'power-gem-explosion';
      explosion.style.left = `${opponentX - 100}px`;
      explosion.style.top = `${opponentY - 100}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      for (let i = 0; i < 18; i++) {
        const burstAngle = (i / 18) * 360;
        const burstDist = 50 + Math.random() * 45;
        const burst = document.createElement('div');
        burst.className = 'power-gem-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 15}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'power-gem-shockwave';
          sw.style.left = `${opponentX - 60}px`;
          sw.style.top = `${opponentY - 60}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('power-gem-user-charge');
      opponentEl.classList.remove('power-gem-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1700);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('power-gem-user-charge');
      opponentEl.classList.remove('power-gem-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

PowerGemAnimation.moveType = 'rock';
